import React, { useState, useEffect } from "react";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Printer, Download, FileDown, QrCode } from "lucide-react";
import { downloadInventoryLabelLbx, updateInventoryItem, getInventoryBadgeLabelPng } from "../../lib/api";

const BADGE_MM_H = 9.9; // zone imprimable bande 12 mm (28 pt)

const TAPE_PRESETS = [
    { key: "12", label: "Bande 12 mm (12 × 40 mm)", h: 12, w: 40, qr: 10 },
    { key: "24", label: "Bande 24 mm (24 × 58 mm)", h: 24, w: 58, qr: 20 },
    { key: "36", label: "Bande 36 mm (36 × 80 mm)", h: 36, w: 80, qr: 30 },
];

function printCss({ W, h, qr, bcMm }) {
    return [
        `@page { size: ${W}mm ${h}mm; margin: 0; }`,
        "html, body { margin: 0; padding: 0; }",
        `.label { width: ${W}mm; height: ${h}mm; display: flex; align-items: center; gap: 1.5mm; padding: 1mm; box-sizing: border-box; font-family: Arial, sans-serif; background: #fff; }`,
        `.label > img { width: ${qr}mm; height: ${qr}mm; }`,
        ".txt { overflow: hidden; }",
        `.tag { font-family: 'Courier New', monospace; font-weight: bold; font-size: ${h >= 24 ? 4 : 2.6}mm; line-height: 1.1; }`,
        `.sub { font-size: ${h >= 24 ? 2.4 : 1.8}mm; line-height: 1.2; }`,
        `.bc { width: ${bcMm}mm; height: ${h >= 24 ? 4.5 : 2.8}mm; margin-top: 0.6mm; display: block; image-rendering: pixelated; }`,
    ].join("\n");
}

// Builds the print document with DOM APIs only (no HTML string injection).
function buildPrintDocument(doc, { code, qrUrl, qrOnly, subtitleLines, bcUrl, withBarcode, ...dims }) {
    const el = (tag, className, text) => {
        const node = doc.createElement(tag);
        if (className) node.className = className;
        if (text !== undefined) node.textContent = text;
        return node;
    };
    const img = (src, className) => { const i = el("img", className); i.src = src; i.alt = ""; return i; };

    doc.head.replaceChildren();
    doc.body.replaceChildren();
    doc.title = code;
    const meta = doc.createElement("meta");
    meta.setAttribute("charset", "utf-8");
    doc.head.append(meta, el("style", null, printCss(dims)));

    const label = el("div", "label");
    label.append(img(qrUrl));
    if (qrOnly) {
        label.style.justifyContent = "center";
        label.style.gap = "0";
    } else {
        const txt = el("div", "txt");
        txt.append(el("div", "tag", code));
        subtitleLines.forEach((l) => txt.append(el("div", "sub", l)));
        txt.append(el("div", "sub", "EGG-TELSA SA"));
        if (withBarcode && bcUrl) txt.append(img(bcUrl, "bc"));
        label.append(txt);
    }
    doc.body.append(label);
}

export function QrLabelDialog({ item, open, onClose, onLabeled }) {
    const [layout, setLayout] = useState("standard");
    const [badgeUrl, setBadgeUrl] = useState("");
    const [badgeDims, setBadgeDims] = useState(null);
    const [tape, setTape] = useState("24");
    const [qrUrl, setQrUrl] = useState("");
    const [downloadingLbx, setDownloadingLbx] = useState(false);
    const [bcUrl, setBcUrl] = useState("");
    const [bcMm, setBcMm] = useState(0);
    const [withBarcode, setWithBarcode] = useState(false);
    const [showModel, setShowModel] = useState(true);
    const [showSerial, setShowSerial] = useState(false);
    const [qrOnly, setQrOnly] = useState(false);
    const [marked, setMarked] = useState(false);
    const preset = TAPE_PRESETS.find((t) => t.key === tape);
    const code = item?.asset_tag || item?.id || "";
    const permanentId = item?.id || "";
    const scanUrl = `${window.location.origin}/scan/${encodeURIComponent(permanentId)}`;

    // Format badge : présélectionné pour la catégorie « badge », aperçu rendu par le serveur (fidèle à l'impression)
    useEffect(() => {
        if (!open) return;
        setLayout(item?.category === "badge" ? "badge" : "standard");
    }, [open, item?.category]);

    useEffect(() => {
        if (!open || !permanentId || layout !== "badge") return;
        let url = "";
        getInventoryBadgeLabelPng(permanentId)
            .then((blob) => {
                url = URL.createObjectURL(blob);
                const i = new Image();
                i.onload = () => setBadgeDims({ w: i.width, h: i.height });
                i.src = url;
                setBadgeUrl(url);
            })
            .catch(() => toast.error("Erreur de génération de l'étiquette badge"));
        return () => { if (url) URL.revokeObjectURL(url); setBadgeUrl(""); };
    }, [open, permanentId, layout]);

    useEffect(() => {
        if (!open || !permanentId) return;
        setMarked(false);
        QRCode.toDataURL(scanUrl, { width: 512, margin: 2, errorCorrectionLevel: "M" })
            .then(setQrUrl)
            .catch(() => toast.error("Erreur de génération du QR code"));
        try {
            const bcCanvas = document.createElement("canvas");
            JsBarcode(bcCanvas, code, { format: "CODE128", displayValue: false, margin: 20, width: 2, height: 80, background: "#ffffff", lineColor: "#000000" });
            setBcMm((bcCanvas.width / 2) * 0.3);
            setBcUrl(bcCanvas.toDataURL("image/png"));
        } catch {
            setBcUrl("");
        }
    }, [open, scanUrl, permanentId, code]);

    if (!item) return null;

    const markLabeled = async () => {
        if (item.labeled || marked) return;
        setMarked(true);
        try {
            await updateInventoryItem(item.id, { labeled: true });
            toast.success("Matériel marqué comme étiqueté");
            if (onLabeled) onLabeled();
        } catch {
            setMarked(false);
        }
    };

    const subtitleLines = [
        showModel && item.model ? String(item.model).slice(0, 40) : null,
        showSerial && item.serial_number ? `SN: ${item.serial_number}`.slice(0, 40) : null,
    ].filter(Boolean);
    const labelWmm = qrOnly
        ? preset.qr + 4
        : (withBarcode && bcUrl && bcMm ? Math.max(preset.w, Math.ceil(preset.qr + 3.5 + bcMm + 1.5)) : preset.w);

    const isBadge = layout === "badge";
    const badgeWmm = badgeDims ? Math.round((badgeDims.w / badgeDims.h) * BADGE_MM_H * 10) / 10 : 0;

    const printBadge = () => {
        const win = window.open("", "_blank", "width=520,height=360");
        if (!win) { toast.error("Autorisez les popups pour imprimer"); return; }
        const doc = win.document;
        doc.head.replaceChildren();
        doc.body.replaceChildren();
        doc.title = code;
        const style = doc.createElement("style");
        style.textContent = `@page { size: ${badgeWmm + 2}mm 12mm; margin: 0; } html, body { margin: 0; padding: 0; } img { display: block; width: ${badgeWmm}mm; height: ${BADGE_MM_H}mm; margin: ${(12 - BADGE_MM_H) / 2}mm 1mm; }`;
        const img = doc.createElement("img");
        img.src = badgeUrl;
        img.alt = code;
        doc.head.append(style);
        doc.body.append(img);
        setTimeout(() => { win.focus(); win.print(); setTimeout(() => win.close(), 500); }, 500);
        markLabeled();
    };

    const downloadBadgePng = () => {
        const a = document.createElement("a");
        a.href = badgeUrl;
        a.download = `etiquette_badge_${code}.png`;
        a.click();
        markLabeled();
    };

    const handlePrint = () => {
        if (isBadge) { printBadge(); return; }
        const win = window.open("", "_blank", "width=520,height=360");
        if (!win) { toast.error("Autorisez les popups pour imprimer"); return; }
        buildPrintDocument(win.document, { code, qrUrl, qrOnly, subtitleLines, bcUrl, withBarcode, W: labelWmm, h: preset.h, qr: preset.qr, bcMm });
        setTimeout(() => {
            win.focus();
            win.print();
            setTimeout(() => win.close(), 500);
        }, 400);
        markLabeled();
    };

    const handleDownloadLbx = async () => {
        setDownloadingLbx(true);
        try {
            const blob = await downloadInventoryLabelLbx(item.id, isBadge ? "12" : tape, qrOnly ? false : withBarcode, { model: qrOnly ? false : showModel, serial: qrOnly ? false : showSerial, qrOnly, layout });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `etiquette_${code}.lbx`;
            a.click();
            URL.revokeObjectURL(a.href);
            toast.success("Fichier .lbx téléchargé — ouvrez-le avec P-touch Editor");
            markLabeled();
        } catch {
            toast.error("Erreur lors de la génération du fichier .lbx");
        } finally {
            setDownloadingLbx(false);
        }
    };

    const handleDownload = () => {
        if (isBadge) { downloadBadgePng(); return; }
        const { h, qr } = preset;
        const PX = 11.811;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(labelWmm * PX);
        canvas.height = Math.round(h * PX);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const load = (src) => new Promise((res) => { if (!src) { res(null); return; } const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });
        Promise.all([load(qrUrl), load(!qrOnly && withBarcode ? bcUrl : "")]).then(([img, bc]) => {
            if (!img) return;
            const pad = Math.round(1 * PX);
            const qrPx = Math.round(qr * PX);
            if (qrOnly) {
                ctx.drawImage(img, Math.round((canvas.width - qrPx) / 2), Math.round((canvas.height - qrPx) / 2), qrPx, qrPx);
                const a = document.createElement("a");
                a.href = canvas.toDataURL("image/png");
                a.download = `etiquette_${code}.png`;
                a.click();
                markLabeled();
                return;
            }
            ctx.drawImage(img, pad, Math.round((canvas.height - qrPx) / 2), qrPx, qrPx);
            ctx.fillStyle = "#000";
            const tx = pad + qrPx + Math.round(1.5 * PX);
            const lines = [code, ...subtitleLines, "EGG-TELSA SA"];
            const yTop = canvas.height * 0.2;
            const yBottom = canvas.height * (bc ? 0.6 : 0.85);
            const step = lines.length > 1 ? (yBottom - yTop) / (lines.length - 1) : 0;
            lines.forEach((line, i) => {
                ctx.font = i === 0
                    ? `bold ${Math.round((h >= 24 ? 4 : 2.6) * PX)}px 'Courier New', monospace`
                    : `${Math.round((h >= 24 ? 2.4 : 1.8) * PX)}px Arial`;
                ctx.fillText(line, tx, Math.round(yTop + i * step));
            });
            if (bc) {
                const bcW = Math.round(bcMm * PX);
                const bcH = Math.round((h >= 24 ? 4.5 : 2.8) * PX);
                ctx.imageSmoothingEnabled = false;
                ctx.drawImage(bc, tx, Math.round(canvas.height * 0.7), bcW, bcH);
                ctx.imageSmoothingEnabled = true;
            }
            const a = document.createElement("a");
            a.href = canvas.toDataURL("image/png");
            a.download = `etiquette_${code}.png`;
            a.click();
            markLabeled();
        });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Étiquette QR — {code}</DialogTitle>
                    <DialogDescription className="sr-only">Aperçu, impression et téléchargement de l'étiquette QR et code-barres du matériel</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-1" data-testid="qr-label-dialog">
                    <div className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full font-medium ${(item.labeled || marked) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500 border border-dashed border-gray-300"}`} data-testid="qr-labeled-status">
                        <QrCode className="w-3 h-3" />
                        {(item.labeled || marked) ? "Ce matériel est marqué comme étiqueté" : "Pas encore étiqueté — sera marqué automatiquement après impression ou téléchargement"}
                    </div>
                    <div>
                        <label className="form-label text-xs">Modèle d'étiquette</label>
                        <div className="grid grid-cols-2 gap-2" data-testid="qr-layout-group">
                            {[["standard", "Standard", "QR + texte horizontal"], ["badge", "Badge 12 mm", "QR + n° série + réf. verticaux"]].map(([k, l, d]) => (
                                <button key={k} type="button" onClick={() => setLayout(k)} data-testid={`qr-layout-${k}`}
                                    className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${layout === k ? "bg-gray-900 text-white border-gray-900" : "bg-white border-gray-300 hover:bg-gray-50"}`}>
                                    <span className="font-medium block">{l}</span>
                                    <span className={`text-[11px] ${layout === k ? "text-gray-300" : "text-gray-500"}`}>{d}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    {isBadge ? (
                        <>
                            <div className="flex justify-center py-3 bg-gray-100 rounded-lg overflow-x-auto">
                                {badgeUrl ? (
                                    <img src={badgeUrl} alt="Étiquette badge" className="bg-white border border-gray-300 shadow-sm" style={{ height: 12 * 11, width: badgeWmm ? (badgeWmm + 2) * 11 : "auto", padding: `${((12 - BADGE_MM_H) / 2) * 11}px 11px`, boxSizing: "border-box" }} data-testid="qr-badge-preview" />
                                ) : <span className="text-xs text-gray-400 py-8">Génération de l'aperçu…</span>}
                            </div>
                            <p className="text-[10px] text-gray-400">
                                Bande continue <strong>12 mm</strong> — longueur {badgeWmm ? `${badgeWmm + 2} mm` : "…"}. Colonnes verticales : {item.serial_number ? "n° de série" : item.model ? "modèle" : "nom"} puis réf. inventaire <strong>{code}</strong>. Le QR encode l'identifiant permanent ({permanentId}). Le fichier <strong>.lbx</strong> s'ouvre directement dans P-touch Editor.
                            </p>
                        </>
                    ) : (
                    <>
                    <div>
                        <label className="form-label text-xs">Format de bande (Brother P-touch)</label>
                        <select value={tape} onChange={(e) => setTape(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="qr-tape-select">
                            {TAPE_PRESETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={qrOnly} onChange={(e) => setQrOnly(e.target.checked)} className="rounded border-gray-300" data-testid="qr-only-checkbox" />
                        QR uniquement (sans texte — étiquette carrée)
                    </label>
                    {!qrOnly && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <span className="text-xs text-gray-500 w-full">Champs de l'étiquette :</span>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={showModel} onChange={(e) => setShowModel(e.target.checked)} className="rounded border-gray-300" data-testid="qr-field-model-checkbox" />
                            Modèle
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={showSerial} onChange={(e) => setShowSerial(e.target.checked)} className="rounded border-gray-300" data-testid="qr-field-serial-checkbox" />
                            N° série
                        </label>
                    </div>
                    )}
                    {!qrOnly && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={withBarcode} onChange={(e) => setWithBarcode(e.target.checked)} className="rounded border-gray-300" data-testid="qr-barcode-checkbox" />
                        Ajouter le code-barres (Asset Tag, lecteur Zebra) — allonge l'étiquette
                    </label>
                    )}
                    <div className="flex justify-center py-3 bg-gray-100 rounded-lg overflow-x-auto">
                        <div className={`bg-white border border-gray-300 shadow-sm flex items-center gap-2 px-2 shrink-0 ${qrOnly ? "justify-center" : ""}`} style={{ width: labelWmm * 4, height: preset.h * 4 }} data-testid="qr-label-preview">
                            {qrUrl && <img src={qrUrl} alt="QR" style={{ width: preset.qr * 4, height: preset.qr * 4 }} />}
                            {!qrOnly && (
                            <div className="overflow-hidden">
                                <p className="font-mono font-bold text-gray-900" style={{ fontSize: preset.h >= 24 ? 15 : 10, lineHeight: 1.1 }}>{code}</p>
                                {subtitleLines.map((l) => (
                                    <p key={l} style={{ fontSize: preset.h >= 24 ? 9 : 7 }} className="text-gray-700 truncate">{l}</p>
                                ))}
                                <p style={{ fontSize: preset.h >= 24 ? 9 : 7 }} className="text-gray-700">EGG-TELSA SA</p>
                                {withBarcode && bcUrl && <img src={bcUrl} alt="Code-barres" style={{ width: bcMm * 4, height: preset.h >= 24 ? 14 : 9, marginTop: 2 }} data-testid="qr-label-barcode" />}
                            </div>
                            )}
                        </div>
                    </div>
                    <p className="text-[10px] text-gray-400">
                        Le QR encode l'<strong>identifiant permanent</strong> du matériel ({permanentId}) : le scan restera valable même si l'Asset Tag est régénéré. Impression : sélectionnez votre Brother P-touch (bande continue {preset.h} mm), ou téléchargez le fichier <strong>.lbx</strong> pour l'ouvrir directement dans P-touch Editor (qualité optimale).
                    </p>
                    </>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={handleDownload} disabled={isBadge ? !badgeUrl : !qrUrl} data-testid="qr-download-btn"><Download className="w-4 h-4 mr-1" />PNG</Button>
                    <Button variant="outline" onClick={handleDownloadLbx} disabled={downloadingLbx} data-testid="qr-download-lbx-btn"><FileDown className="w-4 h-4 mr-1" />{downloadingLbx ? "Génération..." : ".lbx (P-touch Editor)"}</Button>
                    <Button onClick={handlePrint} disabled={isBadge ? !badgeUrl : !qrUrl} data-testid="qr-print-btn"><Printer className="w-4 h-4 mr-1" />Imprimer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
